import { createContext, useReducer, ReactNode, Dispatch, useEffect, useRef } from 'react'
import { initialState, formReducer, FormState, FormAction } from './formReducer'
import { useSearchParams, useLocation } from 'react-router'
import { apiGet } from '../services/apiService'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { trackGeneralEvents } from '@/utils/gtm'
import { ROUTES } from '@/config/routes'

interface FormContextProps {
  state: FormState
  dispatch: Dispatch<FormAction>
}

interface FormProviderProps {
  children: ReactNode
}

const FormContext = createContext<FormContextProps | undefined>(undefined)

const FormProvider = ({ children }: FormProviderProps) => {
  const [state, dispatch] = useReducer(formReducer, initialState)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = usePersistedNavigation()
  const hasNavigatedRef = useRef(false)

  // IMPORTANT: This effect should only run when searchParams changes (e.g., on initial load or when formid changes).
  // DO NOT add location.pathname or other navigation-related dependencies here!
  // Adding location.pathname will cause this effect to re-run on every route change, which can result in unnecessary API calls
  // and override unsaved form data. Only searchParams should be a dependency unless you have a very good reason.
  useEffect(() => {
    const formId = searchParams.get('formid') || searchParams.get('formID') || searchParams.get('formId')
    if (formId) {
      const fetchSavedState = async () => {
        try {
          const result = await apiGet(`Form/GetById?id=${formId}`)
          
          if (result.success && result.data) {
            const savedState = result.data

            // Update form metadata
            dispatch({ 
              type: 'UPDATE_FORM_METADATA', 
              payload: { 
                formId: savedState.formId || formId || null,
                dealerId: savedState.dealerId ? savedState.dealerId : null
              }
            })

            // If isPostCall is true, then update the form metadata with the post call data
            if (savedState.isPostCall) {
              dispatch({ 
                type: 'SET_IS_POST_CALL', 
                payload: savedState.isPostCall
              })
            }

            // Update personal section if it exists
            if (savedState.personal) {
              if (savedState.personal.yourDetails && savedState.personal.yourDetails.firstName !== null) {
                dispatch({ 
                  type: 'UPDATE_PERSONAL', 
                  step: 'yourDetails', 
                  payload: savedState.personal.yourDetails 
                })
              }

              if (savedState.personal.aboutYou) {
                dispatch({ 
                  type: 'UPDATE_PERSONAL', 
                  step: 'aboutYou', 
                  payload: savedState.personal.aboutYou 
                })
              }

              if (savedState.personal.income) {
                dispatch({ 
                  type: 'UPDATE_PERSONAL', 
                  step: 'income', 
                  payload: savedState.personal.income 
                })
              }

              if (savedState.personal.financialPosition) {
                dispatch({ 
                  type: 'UPDATE_PERSONAL', 
                  step: 'financialPosition', 
                  payload: savedState.personal.financialPosition 
                })
              }

              if (savedState.personal.applicationOptions) {
                dispatch({ 
                  type: 'UPDATE_PERSONAL', 
                  step: 'applicationOptions', 
                  payload: savedState.personal.applicationOptions 
                })
              }
            }

            // Update business section if it exists and is not null
            if (savedState.business) {
              if (savedState.business.aboutYourBusiness) {
                dispatch({ 
                  type: 'UPDATE_BUSINESS', 
                  step: 'aboutYourBusiness', 
                  payload: savedState.business.aboutYourBusiness 
                })
              }

              if (savedState.business.aboutYou) {
                dispatch({ 
                  type: 'UPDATE_BUSINESS', 
                  step: 'aboutYou', 
                  payload: savedState.business.aboutYou 
                })
              }

              if (savedState.business.financialPosition) {
                dispatch({ 
                  type: 'UPDATE_BUSINESS', 
                  step: 'financialPosition', 
                  payload: savedState.business.financialPosition 
                })
              }
            }
            
            // Prevent navigation if on /dealer page
            if (location.pathname === ROUTES.DEALER.path) {
              return
            }
            
            if (hasNavigatedRef.current) return

            // If isPostCall is true, then navigate to the the selection page
            if (savedState.isPostCall) {
              hasNavigatedRef.current = true
              if (savedState.personal && savedState.business) {
                navigate(ROUTES.HOME.path)
              } else if (savedState.personal) {
                navigate(ROUTES.PERSONAL.YOUR_DETAILS.path)
              } else if (savedState.business) {
                navigate(ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path)
              }
            } else {
              // Navigate based on saved data
              if (savedState.personal) {
                // Track application restart event
                trackGeneralEvents.applicationRestart()
                hasNavigatedRef.current = true
                navigate(ROUTES.PERSONAL.YOUR_DETAILS.path)
              } else if (savedState.business) {
                hasNavigatedRef.current = true
                navigate(ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path)
              }
            }

          }
        } catch (error) {
          console.error('Error fetching saved form state:', error)
        }
      }
      fetchSavedState()
    }
  }, [searchParams]) // Only searchParams! See comment above.

  return (
    <FormContext.Provider value={{ state, dispatch }}>
      {children}
    </FormContext.Provider>
  )
}

export { FormContext, FormProvider }
